import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { mergeMutationOptions, type MutationOptions } from '@/modules/query/utils';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/api/queryKeys';
import { CLIENT_DASHBOARD_QUERY } from '@/modules/client-dashboard/api/queryKeys';
import type { RemediationRequest as ActionCentreRemediationRequest } from '@/modules/action-centre/api';
import { REMEDIATION_REQUESTS_SEARCH_QUERY } from '@/modules/action-centre/api/queryKeys';
import { refreshAssessmentReviewEventually } from './assessmentReview';
import {
  SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
  SUPPLIER_INFO_QUERY,
  CLIENT_ASSESSMENT_REMEDIATIONS_QUERY,
} from './queryKeys';

type CreateRemediationRequestVariables = {
  supplierID: string;
  connectionID: string;
  controlID: string;
  productID?: string;
  deadline: string;
  reason: string;
};

export function useCreateRemediationRequest(
  options?: MutationOptions<ActionCentreRemediationRequest, CreateRemediationRequestVariables>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ connectionID, controlID, productID, deadline, reason }) {
        const res = await request<ActionCentreRemediationRequest>('POST', '/remediation-requests', {
          connectionID,
          controlID,
          productID,
          deadline,
          reason,
        });
        return res.data;
      },
      async onSuccess(_data, variables) {
        // Requesting remediation on a control auto-dismisses its AI finding.
        void refreshAssessmentReviewEventually(queryClient, variables.supplierID);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [REMEDIATION_REQUESTS_SEARCH_QUERY] }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
        ]);
      },
    }),
  );
}

export enum RemediationRequestAction {
  Cancel = 'cancel',
  Complete = 'complete',
  Reject = 'reject',
  Respond = 'respond',
}

export type RemediationRequestActionResponse = {
  remediationRequest: ActionCentreRemediationRequest | null;
};

type PerformRemediationRequestActionVariables = {
  remediationID: string;
  action: RemediationRequestAction;
  supplierID?: string;
  clientID?: string;
};

export function usePerformRemediationRequestAction(
  options?: MutationOptions<
    RemediationRequestActionResponse,
    PerformRemediationRequestActionVariables
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ remediationID, action }) {
        const res = await request<RemediationRequestActionResponse>(
          'PUT',
          `/remediation-requests/${remediationID}/action`,
          {
            action,
          },
        );
        return res.data;
      },
      async onSuccess(_data, variables) {
        if (variables.supplierID) {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY, variables.supplierID],
            }),
            queryClient.invalidateQueries({
              queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
            }),
            queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
            queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
          ]);
        }

        if (variables.clientID) {
          await queryClient.invalidateQueries({
            queryKey: [CLIENT_ASSESSMENT_REMEDIATIONS_QUERY, variables.clientID],
          });
        }
      },
    }),
  );
}
